'use client';

import Link from 'next/link';
import { BlogPost } from '@prisma/client';
import { BlogCard } from '@/components/blog/BlogCard';
import { Utensils, BookHeart, Gamepad2, GraduationCap, Sparkles, ArrowRight, Star, Heart, Zap } from 'lucide-react';

interface BlogsLandingProps {
  latestPosts: BlogPost[];
}

const CATEGORIES = [
  {
    slug: 'eats',
    title: 'Eats',
    icon: Utensils,
    emoji: '🍴',
    description: 'Delicious recipes & healthy meals for kids',
    gradient: 'linear-gradient(135deg, #F8C8A0 0%, #FFF5EB 100%)',
    hoverGradient: 'linear-gradient(135deg, #FDB988 0%, #FFE5CC 100%)',
    color: '#D97706',
    bgPattern: '0 0 30px 30px',
  },
  {
    slug: 'diary',
    title: 'Diary',
    icon: BookHeart,
    emoji: '📔',
    description: "Amaya's daily adventures & magical moments",
    gradient: 'linear-gradient(135deg, #C9B6E4 0%, #F3EFFA 100%)',
    hoverGradient: 'linear-gradient(135deg, #B89FD8 0%, #E8DEFF 100%)',
    color: '#7C3AED',
    bgPattern: '0 0 25px 25px',
  },
  {
    slug: 'play',
    title: 'Play',
    icon: Gamepad2,
    emoji: '🎮',
    description: 'Fun games & activities for creative kids',
    gradient: 'linear-gradient(135deg, #A8D8E6 0%, #E0F2FE 100%)',
    hoverGradient: 'linear-gradient(135deg, #8BC9DD 0%, #C5E8FD 100%)',
    color: '#0284C7',
    bgPattern: '0 0 35px 35px',
  },
  {
    slug: 'teach',
    title: 'Teach',
    icon: GraduationCap,
    emoji: '📚',
    description: 'Montessori-inspired teaching & learning',
    gradient: 'linear-gradient(135deg, #B4A0E5 0%, #EDE9FE 100%)',
    hoverGradient: 'linear-gradient(135deg, #A088D9 0%, #E0D6FD 100%)',
    color: '#7C3AED',
    bgPattern: '0 0 28px 28px',
  },
];

export function BlogsLanding({ latestPosts }: BlogsLandingProps) {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #FFF9F5 0%, #FFFEFC 50%, #F8F9FE 100%)' }}>
      {/* Hero Section with Animated Background */}
      <section
        style={{
          background: 'linear-gradient(135deg, rgba(255, 181, 167, 0.15) 0%, rgba(168, 216, 230, 0.15) 35%, rgba(201, 182, 228, 0.15) 65%, rgba(181, 242, 224, 0.15) 100%)',
          padding: '5rem 1.5rem 4rem',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Animated Background Elements */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0.05,
            backgroundImage: 'radial-gradient(circle at 20% 50%, #F8C8A0 0%, transparent 50%), radial-gradient(circle at 80% 80%, #A8D8E6 0%, transparent 50%), radial-gradient(circle at 40% 20%, #C9B6E4 0%, transparent 50%)',
            animation: 'float 20s ease-in-out infinite',
          }}
        />
        
        {/* Decorative Floating Stars */}
        <Star style={{ position: 'absolute', top: '10%', left: '10%', width: '32px', height: '32px', color: '#F8C8A0', opacity: 0.3, animation: 'float 6s ease-in-out infinite' }} />
        <Star style={{ position: 'absolute', top: '20%', right: '15%', width: '24px', height: '24px', color: '#A8D8E6', opacity: 0.4, animation: 'float 8s ease-in-out infinite 1s' }} />
        <Star style={{ position: 'absolute', bottom: '25%', left: '20%', width: '28px', height: '28px', color: '#C9B6E4', opacity: 0.35, animation: 'float 7s ease-in-out infinite 2s' }} />
        <Star style={{ position: 'absolute', bottom: '15%', right: '10%', width: '20px', height: '20px', color: '#B5F2E0', opacity: 0.3, animation: 'float 9s ease-in-out infinite 1.5s' }} />
        
        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.75rem', 
              marginBottom: '1.5rem',
              animation: 'fadeInUp 0.8s ease-out'
            }}>
              <Sparkles size={32} style={{ color: '#F59E0B', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }} />
              <h1
                style={{
                  fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #FF6B9D 0%, #C084FC 25%, #60A5FA 50%, #34D399 75%, #FBBF24 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  lineHeight: 1.1,
                  margin: 0,
                  letterSpacing: '-0.02em',
                  textShadow: '0 0 40px rgba(255, 107, 157, 0.3)',
                }}
              >
                Amaya's Magical Blog
              </h1>
              <Sparkles size={32} style={{ color: '#34D399', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite 0.5s' }} />
            </div>
            <p
              style={{
                fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
                color: '#64748b',
                fontWeight: 600,
                maxWidth: '750px',
                margin: '0 auto 2rem',
                lineHeight: 1.6,
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              Explore recipes, adventures, games, and learning activities for creative families
            </p>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.2) 0%, rgba(201, 182, 228, 0.2) 100%)',
              backdropFilter: 'blur(10px)',
              borderRadius: '50px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              fontSize: '0.9375rem',
              fontWeight: 700,
              color: '#7C3AED',
              animation: 'fadeInUp 0.8s ease-out 0.4s both',
            }}>
              <Star size={16} fill="#F59E0B" color="#F59E0B" />
              Premium Educational Content
            </div>
          </div>

          {/* Category Cards Grid - 2026 Award-Winning Design */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '1.5rem',
              marginTop: '3rem',
              animation: 'fadeInUp 0.8s ease-out 0.6s both',
            }}
            className="blog-categories-grid"
          >
            {CATEGORIES.map((category, index) => {
              const Icon = category.icon;
              return (
                <Link
                  key={category.slug}
                  href={`/blogs/${category.slug}`}
                  style={{
                    textDecoration: 'none',
                    display: 'block',
                    animation: `fadeInUp 0.6s ease-out ${0.8 + index * 0.1}s both`,
                  }}
                >
                  <div
                    className="category-card"
                    style={{
                      background: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(30px)',
                      borderRadius: '32px',
                      padding: '3rem 2rem 2.5rem',
                      textAlign: 'center',
                      border: '2px solid rgba(255, 255, 255, 0.6)',
                      boxShadow: '0 20px 60px -15px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2) inset',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-16px) scale(1.03)';
                      e.currentTarget.style.boxShadow = `0 30px 80px -20px ${category.color}50, 0 0 0 2px ${category.color}30, 0 0 40px ${category.color}20`;
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
                      
                      const iconContainer = e.currentTarget.querySelector('.category-icon-container') as HTMLElement;
                      if (iconContainer) {
                        iconContainer.style.transform = 'scale(1.15) rotate(5deg)';
                      }
                      
                      const iconBg = e.currentTarget.querySelector('.category-icon-bg') as HTMLElement;
                      if (iconBg) {
                        iconBg.style.transform = 'scale(1.2)';
                        iconBg.style.opacity = '1';
                      }
                      
                      const icon = e.currentTarget.querySelector('.category-icon-svg') as HTMLElement;
                      if (icon) {
                        icon.style.transform = 'scale(1.1) rotate(-5deg)';
                        icon.style.filter = 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.2))';
                      }
                      
                      const particles = e.currentTarget.querySelectorAll('.category-particle');
                      particles.forEach((particle, i) => {
                        (particle as HTMLElement).style.opacity = '1';
                        (particle as HTMLElement).style.transform = `translate(${Math.cos(i * 60 * Math.PI / 180) * 80}px, ${Math.sin(i * 60 * Math.PI / 180) * 80}px) scale(1)`;
                      });
                      
                      const shine = e.currentTarget.querySelector('.category-shine') as HTMLElement;
                      if (shine) {
                        shine.style.opacity = '1';
                        shine.style.transform = 'translateX(200%)';
                      }
                      
                      const arrow = e.currentTarget.querySelector('.category-arrow') as HTMLElement;
                      if (arrow) {
                        arrow.style.opacity = '1';
                        arrow.style.transform = 'translateX(8px)';
                      }
                      
                      const overlay = e.currentTarget.querySelector('.category-overlay') as HTMLElement;
                      if (overlay) {
                        overlay.style.opacity = '0.05';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 20px 60px -15px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.2) inset';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.7)';
                      
                      const iconContainer = e.currentTarget.querySelector('.category-icon-container') as HTMLElement;
                      if (iconContainer) {
                        iconContainer.style.transform = 'scale(1) rotate(0deg)';
                      }
                      
                      const iconBg = e.currentTarget.querySelector('.category-icon-bg') as HTMLElement;
                      if (iconBg) {
                        iconBg.style.transform = 'scale(1)';
                        iconBg.style.opacity = '0.15';
                      }
                      
                      const icon = e.currentTarget.querySelector('.category-icon-svg') as HTMLElement;
                      if (icon) {
                        icon.style.transform = 'scale(1) rotate(0deg)';
                        icon.style.filter = 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))';
                      }
                      
                      const particles = e.currentTarget.querySelectorAll('.category-particle');
                      particles.forEach((particle) => {
                        (particle as HTMLElement).style.opacity = '0';
                        (particle as HTMLElement).style.transform = 'translate(0, 0) scale(0.5)';
                      });
                      
                      const shine = e.currentTarget.querySelector('.category-shine') as HTMLElement;
                      if (shine) {
                        shine.style.opacity = '0';
                        shine.style.transform = 'translateX(-200%)';
                      }
                      
                      const arrow = e.currentTarget.querySelector('.category-arrow') as HTMLElement;
                      if (arrow) {
                        arrow.style.opacity = '0';
                        arrow.style.transform = 'translateX(-8px)';
                      }
                      
                      const overlay = e.currentTarget.querySelector('.category-overlay') as HTMLElement;
                      if (overlay) {
                        overlay.style.opacity = '0';
                      }
                    }}
                  >
                    {/* Gradient Overlay on Hover */}
                    <div
                      className="category-overlay"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: category.gradient,
                        opacity: 0,
                        transition: 'opacity 0.5s ease',
                        pointerEvents: 'none',
                        zIndex: 0,
                      }}
                    />
                    
                    {/* Shine Effect */}
                    <div
                      className="category-shine"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: '-200%',
                        width: '100%',
                        height: '100%',
                        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)',
                        opacity: 0,
                        transform: 'translateX(-200%)',
                        transition: 'all 0.8s ease',
                        pointerEvents: 'none',
                        zIndex: 2,
                      }}
                    />
                    
                    {/* Icon Container with Floating Particles */}
                    <div style={{ position: 'relative', marginBottom: '2rem', zIndex: 1 }}>
                      {/* Floating Particles around icon */}
                      {[...Array(6)].map((_, i) => (
                        <div
                          key={i}
                          className="category-particle"
                          style={{
                            position: 'absolute',
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            background: category.gradient,
                            top: '50%',
                            left: '50%',
                            marginTop: '-6px',
                            marginLeft: '-6px',
                            opacity: 0,
                            transform: 'translate(0, 0) scale(0.5)',
                            transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${i * 0.05}s`,
                            boxShadow: `0 4px 12px ${category.color}40`,
                            pointerEvents: 'none',
                          }}
                        />
                      ))}
                      
                      {/* Animated Background Glow */}
                      <div
                        className="category-icon-bg"
                        style={{
                          position: 'absolute',
                          width: '140px',
                          height: '140px',
                          borderRadius: '50%',
                          background: `radial-gradient(circle, ${category.color}30 0%, transparent 70%)`,
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%) scale(1)',
                          opacity: 0.15,
                          filter: 'blur(20px)',
                          transition: 'all 0.6s ease',
                          pointerEvents: 'none',
                          animation: 'pulse 3s ease-in-out infinite',
                        }}
                      />
                      
                      {/* Main Icon Container */}
                      <div
                        className="category-icon-container"
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '32px',
                          background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(10px)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          boxShadow: `0 20px 50px -15px ${category.color}60, 0 0 0 1px rgba(255, 255, 255, 0.5) inset, 0 1px 2px rgba(255, 255, 255, 1) inset`,
                          transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                          position: 'relative',
                          border: `3px solid ${category.color}20`,
                        }}
                      >
                        {/* Inner glow */}
                        <div style={{
                          position: 'absolute',
                          inset: '8px',
                          borderRadius: '26px',
                          background: category.gradient,
                          opacity: 0.1,
                        }} />
                        
                        {/* Icon with drop shadow */}
                        <Icon 
                          className="category-icon-svg"
                          size={52} 
                          strokeWidth={2.5}
                          color={category.color}
                          style={{
                            position: 'relative',
                            zIndex: 1,
                            filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.15))',
                            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        />
                      </div>
                    </div>
                    
                    <h2
                      style={{
                        fontSize: '2rem',
                        fontWeight: 900,
                        color: category.color,
                        marginBottom: '1rem',
                        letterSpacing: '-0.02em',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.75rem',
                        textShadow: `0 2px 8px ${category.color}20`,
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {category.title}
                      <ArrowRight 
                        className="category-arrow"
                        size={26} 
                        strokeWidth={3}
                        style={{ 
                          opacity: 0, 
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          transform: 'translateX(-8px)',
                        }} 
                      />
                    </h2>
                    <p
                      style={{
                        fontSize: '1.0625rem',
                        color: '#64748b',
                        lineHeight: 1.7,
                        fontWeight: 500,
                        position: 'relative',
                        zIndex: 1,
                      }}
                    >
                      {category.description}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest Posts Section */}
      <section style={{ padding: '5rem 1.5rem', background: 'transparent' }}>
        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem', position: 'relative' }}>
            {/* Decorative badge */}
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '8px', 
              background: 'linear-gradient(135deg, rgba(180, 160, 229, 0.15), rgba(168, 216, 230, 0.15))',
              padding: '8px 20px',
              borderRadius: '50px',
              border: '2px solid rgba(180, 160, 229, 0.3)',
              marginBottom: '24px',
              animation: 'fadeInUp 0.8s ease-out 0.2s backwards',
            }}>
              <Sparkles size={18} color="#B4A0E5" style={{ animation: 'pulse 2s ease-in-out infinite' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#B4A0E5', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Fresh from the blog
              </span>
            </div>

            <h2
              style={{
                fontSize: 'clamp(2.5rem, 5vw, 4rem)',
                fontWeight: 900,
                marginBottom: '1rem',
                background: 'linear-gradient(135deg, #B4A0E5 0%, #A8D8E6 25%, #F8C8A0 50%, #C9B6E4 75%, #B4A0E5 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                backgroundSize: '200% 200%',
                animation: 'gradientFlow 6s ease infinite, fadeInUp 0.8s ease-out 0.3s backwards',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                position: 'relative',
              }}
            >
              Latest Adventures
            </h2>
            
            <p style={{
              fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
              color: '#64748b',
              fontWeight: 500,
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6,
              animation: 'fadeInUp 0.8s ease-out 0.4s backwards',
            }}>
              Discover <strong style={{ color: '#B4A0E5' }}>magical stories</strong>, <strong style={{ color: '#A8D8E6' }}>fun activities</strong>, and <strong style={{ color: '#F8C8A0' }}>learning adventures</strong>
            </p>

            {/* Floating sparkles decoration */}
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  width: `${Math.random() * 6 + 3}px`,
                  height: `${Math.random() * 6 + 3}px`,
                  background: ['#B4A0E5', '#A8D8E6', '#F8C8A0'][i % 3],
                  borderRadius: '50%',
                  top: `${20 + Math.random() * 60}%`,
                  left: `${10 + Math.random() * 80}%`,
                  animation: `float ${8 + Math.random() * 4}s ease-in-out infinite ${Math.random() * 2}s`,
                  opacity: 0.4,
                  boxShadow: `0 0 8px currentColor`,
                  pointerEvents: 'none',
                }}
              />
            ))}
          </div>

          {latestPosts.length === 0 ? (
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(30px)',
                borderRadius: '48px',
                padding: 'clamp(3rem, 8vw, 6rem) clamp(2rem, 5vw, 4rem)',
                textAlign: 'center',
                border: '3px solid rgba(180, 160, 229, 0.2)',
                boxShadow: '0 30px 80px -20px rgba(180, 160, 229, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
                position: 'relative',
                overflow: 'hidden',
                animation: 'fadeInUp 0.8s ease-out 1s both',
              }}
            >
              {/* Animated background orbs */}
              <div style={{
                position: 'absolute',
                top: '-30%',
                left: '-20%',
                width: '300px',
                height: '300px',
                background: 'radial-gradient(circle, rgba(168, 216, 230, 0.2) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                animation: 'float 12s ease-in-out infinite',
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-20%',
                right: '-15%',
                width: '250px',
                height: '250px',
                background: 'radial-gradient(circle, rgba(201, 182, 228, 0.25) 0%, transparent 70%)',
                borderRadius: '50%',
                filter: 'blur(40px)',
                animation: 'float 15s ease-in-out infinite 2s',
              }} />
              
              {/* Floating sparkles */}
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    width: `${Math.random() * 6 + 3}px`,
                    height: `${Math.random() * 6 + 3}px`,
                    background: ['#B4A0E5', '#A8D8E6', '#F8C8A0', '#C9B6E4'][i % 4],
                    borderRadius: '50%',
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animation: `float ${10 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 5}s`,
                    opacity: 0.4,
                    boxShadow: `0 0 ${Math.random() * 10 + 5}px currentColor`,
                  }}
                />
              ))}
              
              {/* Main icon with glow */}
              <div style={{ position: 'relative', marginBottom: '2.5rem', display: 'inline-block' }}>
                {/* Pulsing glow background */}
                <div style={{
                  position: 'absolute',
                  width: '200px',
                  height: '200px',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  background: 'radial-gradient(circle, rgba(180, 160, 229, 0.3) 0%, transparent 70%)',
                  filter: 'blur(30px)',
                  animation: 'pulse 3s ease-in-out infinite',
                  pointerEvents: 'none',
                }} />
                
                {/* Icon container */}
                <div style={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '40px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  boxShadow: '0 20px 60px -15px rgba(180, 160, 229, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                  position: 'relative',
                  border: '3px solid rgba(180, 160, 229, 0.2)',
                  animation: 'float 4s ease-in-out infinite',
                }}>
                  {/* Inner gradient glow */}
                  <div style={{
                    position: 'absolute',
                    inset: '10px',
                    borderRadius: '32px',
                    background: 'linear-gradient(135deg, #B4A0E5, #A8D8E6)',
                    opacity: 0.15,
                  }} />
                  
                  <Sparkles 
                    size={64} 
                    strokeWidth={2.5}
                    color="#B4A0E5"
                    style={{
                      position: 'relative',
                      zIndex: 1,
                      filter: 'drop-shadow(0 4px 12px rgba(180, 160, 229, 0.4))',
                      animation: 'pulse 2s ease-in-out infinite',
                    }}
                  />
                </div>
              </div>
              
              <h3
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3rem)',
                  fontWeight: 900,
                  marginBottom: '1.5rem',
                  background: 'linear-gradient(135deg, #B4A0E5 0%, #A8D8E6 50%, #F8C8A0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  backgroundSize: '200% 200%',
                  animation: 'gradientFlow 5s ease infinite',
                  position: 'relative',
                  zIndex: 1,
                  letterSpacing: '-0.02em',
                  textShadow: '0 4px 20px rgba(180, 160, 229, 0.2)',
                }}
              >
                Amazing Content Coming Soon!
              </h3>
              
              <p
                style={{
                  fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                  color: '#64748b',
                  maxWidth: '700px',
                  margin: '0 auto 2rem',
                  lineHeight: 1.8,
                  fontWeight: 500,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                We're crafting <strong style={{ color: '#B4A0E5' }}>wonderful recipes</strong>, 
                <strong style={{ color: '#C9B6E4' }}> magical stories</strong>, 
                <strong style={{ color: '#A8D8E6' }}> fun games</strong>, and 
                <strong style={{ color: '#F8C8A0' }}> teaching ideas</strong> just for you!
              </p>
              
              {/* Badge grid */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1rem',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                zIndex: 1,
              }}>
                {[
                  { icon: <Sparkles size={18} />, text: 'Hand-crafted', color: '#B4A0E5' },
                  { icon: <Heart size={18} />, text: 'With Love', color: '#EF4444' },
                  { icon: <Star size={18} />, text: 'Premium Quality', color: '#F59E0B' },
                  { icon: <Zap size={18} />, text: 'Coming Soon', color: '#10B981' },
                ].map((badge, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.75rem 1.5rem',
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '16px',
                      border: `2px solid ${badge.color}20`,
                      fontSize: '0.9375rem',
                      fontWeight: 700,
                      color: badge.color,
                      boxShadow: `0 4px 16px ${badge.color}15`,
                      animation: `fadeInUp 0.6s ease-out ${1.2 + i * 0.1}s both`,
                    }}
                  >
                    {badge.icon}
                    <span>{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: '2.5rem',
              }}
            >
              {latestPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-20px) scale(1.02);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes gradientFlow {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        /* Responsive grid for blog categories */
        @media (max-width: 1200px) {
          .blog-categories-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1.5rem !important;
          }
        }

        @media (max-width: 768px) {
          .blog-categories-grid {
            grid-template-columns: 1fr !important;
            gap: 1.25rem !important;
          }
        }

        @media (max-width: 1024px) {
          section {
            padding: 3.5rem 1.25rem !important;
          }
        }

        @media (max-width: 768px) {
          section {
            padding: 2.5rem 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}

